import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Check, Star } from 'lucide-react';
import Navbar from '../components/Navbar';
import { FooterLogo } from '../components/BookersLogo';
import { Footer } from '../components/FooterCTA';
import useReveal from '../hooks/useReveal';
import { toast } from 'sonner';
import axios from 'axios';
import { useI18n } from '../i18n/I18nProvider';

const API = process.env.REACT_APP_BACKEND_URL;
const RESTAURANT_IMAGE = 'https://images.unsplash.com/photo-1636405189493-181ecf851006?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxlbGVnYW50JTIwcmVzdGF1cmFudCUyMGludGVyaW9yJTIwd2FybSUyMGxpZ2h0aW5nfGVufDB8fHx8MTc5MDYwMTI1MHww&ixlib=rb-4.1.0&q=85';

export default function SignupPage() {
  const { t } = useI18n();
  const [form, setForm] = useState({ nom: '', prenom: '', restaurant_name: '', telephone: '', address: '', message: '', consent: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  useReveal();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom || !form.prenom || !form.restaurant_name || !form.telephone) {
      toast.error(t('signup.toastRequired'));
      return;
    }
    if (!form.consent) {
      toast.error(t('signup.toastConsent'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/api/leads/signup`, form);
      setDone(true);
      toast.success(t('signup.toastSuccess'));
    } catch {
      toast.error(t('signup.toastError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#fafafa]">
        <div className="grid lg:grid-cols-2 min-h-screen">
          <div className="hidden lg:block relative">
            <img src={RESTAURANT_IMAGE} alt={t('signup.imageAlt')} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10" />
            <div className="absolute bottom-0 left-0 right-0 p-12">
              <div className="mb-4">
                <FooterLogo className="h-10" />
              </div>
              <h2 className="font-display text-3xl md:text-4xl text-white max-w-md">{t('signup.panelTitle')}</h2>
              <p className="text-white/70 mt-3 text-lg max-w-md">{t('signup.panelSub')}</p>
              <div className="flex items-center gap-3 mt-6">
                <div className="flex -space-x-2">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-9 w-9 rounded-full bg-white/20 backdrop-blur border-2 border-white/40 flex items-center justify-center text-white text-xs font-bold">
                      {['MR', 'EF', 'DC'][i]}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5">
                    {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={12} className="fill-[#f5b301] text-[#f5b301]" />)}
                  </div>
                  <p className="text-white/60 text-xs mt-0.5">{t('signup.rated')}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center px-5 py-28 lg:px-12 lg:py-20">
            <div className="w-full max-w-lg">
              {!done ? (
                <>
                  <p className="text-sm font-semibold text-[#76B828]">{t('signup.eyebrow')}</p>
                  <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0b0b0f]">{t('signup.title')}</h1>
                  <p className="text-[#55555f] mt-3 text-[15px]">{t('signup.subtitle')}</p>

                  <form onSubmit={submit} className="mt-8 space-y-5" data-testid="signup-form">
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('signup.restaurant')} *</label>
                      <input value={form.restaurant_name} onChange={update('restaurant_name')} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('signup.placeholderRestaurant')} data-testid="signup-restaurant" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-sm font-medium text-[#3a3a42]">{t('signup.lastName')} *</label>
                        <input value={form.nom} onChange={update('nom')} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('signup.placeholderLast')} data-testid="signup-nom" />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-[#3a3a42]">{t('signup.firstName')} *</label>
                        <input value={form.prenom} onChange={update('prenom')} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('signup.placeholderFirst')} data-testid="signup-prenom" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('signup.phone')} *</label>
                      <input value={form.telephone} onChange={update('telephone')} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('signup.placeholderPhone')} data-testid="signup-telephone" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('signup.address')}</label>
                      <input value={form.address} onChange={update('address')} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition" placeholder={t('signup.placeholderAddress')} data-testid="signup-address" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#3a3a42]">{t('signup.message')}</label>
                      <textarea value={form.message} onChange={update('message')} rows={3} className="mt-1.5 w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[15px] outline-none focus:border-[#76B828] focus:ring-2 focus:ring-[#76B828]/20 transition resize-none" placeholder={t('signup.placeholderMessage')} data-testid="signup-message" />
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer group" data-testid="signup-consent-label">
                      <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 h-4 w-4 rounded border-black/20 text-[#76B828] focus:ring-[#76B828]/30" data-testid="signup-consent" />
                      <span className="text-[13px] text-[#6b6b73] leading-relaxed">
                        {t('signup.consentPrefix')}
                        <Link to="/privacy" className="underline hover:text-[#76B828]">{t('signup.privacyLink')}</Link>
                        {t('signup.consentSuffix')}
                      </span>
                    </label>

                    <button type="submit" disabled={loading} className="btn-pill btn-brand w-full py-3.5 text-[15px] disabled:opacity-70 group" data-testid="signup-submit">
                      {loading ? (<><Loader2 size={18} className="animate-spin" /> {t('signup.sending')}</>) : (<>{t('signup.submit')} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" /></>)}
                    </button>
                  </form>

                  <p className="text-center text-[13px] text-[#8b8b95] mt-6">{t('signup.footnote')}</p>
                </>
              ) : (
                <div className="text-center py-12" data-testid="signup-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[#e9f9f0] flex items-center justify-center">
                    <Check size={30} className="text-[#16a34a]" />
                  </div>
                  <h2 className="font-display text-2xl mt-5 text-[#0b0b0f]">{t('signup.successTitle')}</h2>
                  <p className="text-[#6b6b73] mt-2 max-w-sm mx-auto">{t('signup.successBody', '', { name: form.prenom || '' })}</p>
                  <Link to="/" className="btn-pill btn-dark px-8 py-3 mt-6 inline-flex">{t('signup.backHome')}</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
